import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import emitter from '../emitter';
import {
  DISMISS_POPUP,
  SHOW_CONF,
  PICKUP_ITEM,
  SHOW_POPUP_ERROR,
  SHOW_POPUP_INTRO,
} from '../settings/events';
import { TransitionGroup } from 'react-transition-group';
import BrorTransition from './Transition';
import platform from '../util/platform';

import ConfMenu from './ConfMenu';
import Modal from './Modal';
import Popup from './Popup';
import { Icon } from './Icon';

import { BOTTOM, TOP, LEFT } from './Popup/alignments';
import popupStyles from './Popup/Popup.module.less';
import {
  actionConfOpened,
  thunkConfClosed,
  actionItemPickedUp,
  actionClearPreviousConf,
} from '../state/popups';
import { translate } from '../services/L10n';
import productService from '../services/products';
import { getMenuItems } from './ConfMenu/getMenuItems';
import { selectTac } from '../state/tac/tacSelectors.ts';
import { selectIsWallResizerActive } from '../state/scene/sceneSelectors.ts';
import { selectUserAgent } from '../state/userAgent/userAgentSelectors';
import {
  selectConfDialog,
  selectHasShownExtendableConf,
  selectHasShownPegboardHint,
  selectHasShownCuttableMountingRailHint,
  selectIntroPopupsVisible,
  selectPegboardHint,
  selectCuttableMountingRailHint,
  selectCuttableMountingRailHintAlignment,
  selectShowDoorsPopupHint,
  selectShowExtendableConf,
} from '../state/popups/popupsSelectors';
import localStatisticsReporter from '../services/statistics/insights/custom/local/localStatisticsReporter';
import { t } from '../translations';

const types = {
  CONF: 'CONF',
  ERROR: 'ERROR',
  INTRO: 'INTRO',
  INFO: 'INFO',
};

class ScenePopupManager extends React.Component {
  static propTypes = {
    introPopupsVisible: PropTypes.bool,
    pegboardPopupHintTarget: PropTypes.object,
    cuttableMountingRailPopupHintTarget: PropTypes.object,
    cuttableMountingRailPopupHintAlignment: PropTypes.string,
    doorsPopupHintTarget: PropTypes.object,
    confDialog: PropTypes.object,
    userAgent: PropTypes.object,
    confOpened: PropTypes.func.isRequired,
    confClosed: PropTypes.func.isRequired,
    clearPreviousConf: PropTypes.func.isRequired,
    itemPickedUp: PropTypes.func.isRequired,
    getItemSceneBounds: PropTypes.func,
    getSprite: PropTypes.func,
    tac: PropTypes.object,
    wallResizerActive: PropTypes.bool,
  };

  static getDerivedStateFromProps(props, state) {
    if (!props.introPopupsVisible) {
      return {
        ...state,
        popups: state.popups.filter(popup => popup.type !== types.INTRO),
      };
    }
    return state;
  }

  state = {
    popups: [],
  };

  constructor(...args) {
    super(...args);

    // This component should never be unmounted.
    emitter.on(SHOW_CONF, this.onShowConf);
    emitter.on(PICKUP_ITEM, this.onItemPickup);

    emitter.on(SHOW_POPUP_ERROR, this.onShowError);
    emitter.on(SHOW_POPUP_INTRO, this.onShowIntro);
    emitter.on(DISMISS_POPUP, this.onPopupClose);
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      nextProps.userAgent !== this.props.userAgent ||
      nextProps.introPopupsVisible !== this.props.introPopupsVisible ||
      nextProps.pegboardPopupHintTarget !==
        this.props.pegboardPopupHintTarget ||
      nextProps.cuttableMountingRailPopupHintTarget !==
        this.props.cuttableMountingRailPopupHintTarget ||
      nextProps.cuttableMountingRailPopupHintAlignment !==
        this.props.cuttableMountingRailPopupHintAlignment ||
      nextProps.doorsPopupHintTarget !== this.props.doorsPopupHintTarget ||
      nextProps.extendableConfTarget !== this.props.extendableConfTarget ||
      nextProps.extendableConfShown !== this.props.extendableConfShown ||
      nextProps.getItemSceneBounds !== this.props.getItemSceneBounds ||
      nextProps.getSprite !== this.props.getSprite ||
      nextState.popups !== this.state.popups
    );
  }

  /**
   * Calls the individual methods for handling item intro popups and extendable
   * item intro popup.
   * @param {Object} prevProps The previous props from before the component update,
   *                           or null if there are no previous props.
   */
  handleItemIntros(prevProps) {
    this.handleItemIntro(prevProps, {
      popupHintTargetPropName: 'pegboardPopupHintTarget',
      translationKey: 'POPUP_PEGBOARD_HINT',
      alignment: LEFT,
    });
    this.handleItemIntro(prevProps, {
      popupHintTargetPropName: 'cuttableMountingRailPopupHintTarget',
      translationKey: 'MOUNTING_RAIL_CUT',
      alignment: this.props.cuttableMountingRailPopupHintAlignment,
    });
    this.handleItemIntro(prevProps, {
      popupHintTargetPropName: 'doorsPopupHintTarget',
      translationKey: 'POPUP_INFORMATION_DOORS',
      alignment: TOP,
    });
    this.handleExtendableItemIntro(prevProps);
  }

  /**
   * Handles the introduction popup for a type of item, as configured by
   * the settings parameter.
   * @param {Object} prevProps The previous props from before the component update,
   *                           or null if there are no previous props.
   * @param {Object} settings Settings for the popup.
   */
  handleItemIntro(prevProps, settings) {
    const popupHintTarget = this.props[settings.popupHintTargetPropName];
    const prevPopupHintTarget = prevProps
      ? prevProps[settings.popupHintTargetPropName]
      : null;
    const popupHintTargetHasChanged =
      popupHintTarget?.itemid !== prevPopupHintTarget?.itemid;
    const confMenuHasBeenOpened =
      !prevProps?.confDialog?.open && this.props.confDialog?.open;

    // Add new hint popup
    if (popupHintTarget && popupHintTargetHasChanged) {
      this.onShowInfo(
        {
          item: {
            ...popupHintTarget,
          },
        },
        translate(t[settings.translationKey]),
        {
          dismissible: false,
          alignment: settings.alignment,
        }
      );
    }

    // Delete old hint popup
    if (
      prevPopupHintTarget &&
      (popupHintTargetHasChanged || confMenuHasBeenOpened)
    ) {
      const removeKey = `${types.INFO}-${prevPopupHintTarget.itemid}`;
      this.setState({
        popups: this.state.popups.filter(popup => popup.key !== removeKey),
      });
    }
  }

  handleExtendableItemIntro(prevProps) {
    const { extendableConfTarget } = this.props;
    const extendableConfShown = prevProps
      ? prevProps.extendableConfShown
      : false;
    if (extendableConfTarget && !extendableConfShown) {
      setTimeout(() => {
        const sprite = this.props.getSprite(extendableConfTarget);
        if (!sprite) {
          console.log('sprite missing');
        }
        this.onShowConf(sprite);
      });
    }
  }

  componentDidMount() {
    this.handleItemIntros(null);
  }

  componentDidUpdate(prevProps) {
    const { wallResizerActive } = this.props;
    const { isMobile, isPortrait } = this.props.userAgent;

    if (
      (isMobile && isPortrait) !==
      (prevProps.userAgent.isMobile && prevProps.userAgent.isPortrait)
    ) {
      this.closeConf();
    }

    if (wallResizerActive && !prevProps.wallResizerActive) {
      this.closeErrors();
    }

    this.handleItemIntros(prevProps);
  }

  componentWillUnmount() {
    emitter.off(SHOW_CONF, this.onShowConf);
    emitter.off(SHOW_POPUP_ERROR, this.onShowError);
    emitter.off(SHOW_POPUP_INTRO, this.onShowIntro);
    emitter.off(DISMISS_POPUP, this.onPopupClose);
    emitter.off(PICKUP_ITEM, this.onItemPickup);
  }

  getChild(items, childItemid) {
    return items.filter(item => item.itemid === childItemid);
  }

  onShowConf = sprite => {
    const { isMobile, isPortrait } = this.props.userAgent;
    const { confDialog } = this.props;
    let selectSprite = sprite;

    if (typeof this.previousConfTimeout !== 'undefined') {
      clearTimeout(this.previousConfTimeout);
    }

    const Component = isMobile && isPortrait ? Modal : Popup;
    if (confDialog) {
      if (sprite?.item?.itemid === confDialog.closedId) {
        if (
          sprite.parentItem.itemid &&
          !productService.isMultiParentProduct(sprite.item) &&
          !productService.isTable(sprite.item)
        ) {
          const parentSprite = sprite.parent.parent;
          const parentChild = this.getChild(
            parentSprite.item.items,
            sprite.item.itemid
          );
          if (parentChild.length > 0) {
            selectSprite = parentSprite;
          }
        } else if (sprite.childrenContainer) {
          const children = sprite.childrenContainer.children;
          const matchingChild = this.getChild(children, confDialog.closedId);
          if (matchingChild.length !== 0) {
            selectSprite = matchingChild;
          }
        }
      }
    }

    const key = `${types.CONF}-${selectSprite.item.itemid}`;

    const menuItems = getMenuItems(this.props.tac, selectSprite);
    if (!menuItems.showConfMenu) {
      return;
    }
    this.openPopup({
      sprite: selectSprite,
      Component,
      content: (
        <ConfMenu
          container={selectSprite}
          onDelete={() => this.onPopupClose(key)}
        />
      ),
      type: types.CONF,
      props: {
        container: '#mobileMenu',
        autoWidth: true,
        outlineTarget: true,
        alignment: isMobile ? BOTTOM : TOP,
        spacing: platform.isKiosk ? 15 : 10,
      },
      key,
    });

    localStatisticsReporter.reportTacArticleClicked(sprite.item.id);
    this.props.confClosed();
    this.props.confOpened();
  };

  onItemPickup = sprite => {
    const item = sprite.item;
    this.props.itemPickedUp(item);
  };

  onShowError = (sprite, message, props) => {
    const content = (
      <React.Fragment>
        <Icon className={popupStyles.icon} iconName="IncorrectIcon" />
        {translate(t[message])}
      </React.Fragment>
    );
    this.openPopup({ sprite, content, type: types.ERROR });
  };

  onShowIntro = (sprite, content) => {
    if (this.props.introPopupsVisible) {
      this.openPopup({
        sprite,
        content,
        type: types.INTRO,
        props: { wiggle: true, dismissible: false },
      });
    }
  };

  onShowInfo = (sprite, content, props) => {
    const key = types.INFO;
    this.openPopup({ sprite, content, type: types.INFO, props, key });
  };

  calculateTargetRect(sprite) {
    if (sprite._destroyed) {
      return;
    }

    // TODO: This should be handled differently -- extremely ugly
    const canvas = document.getElementsByTagName('canvas')[0];

    if (!canvas) {
      throw new Error('No canvas element found');
    }

    const canvasPos = canvas.getBoundingClientRect();

    const itemSceneBounds = this.props.getItemSceneBounds(sprite.item);

    if (!itemSceneBounds) {
      // Item is not on scene.
      return;
    }

    const { height, width, x, y } = itemSceneBounds;
    const offsetX = x + canvasPos.left;
    const offsetY = y + canvasPos.top;

    return {
      width,
      height,
      top: offsetY,
      left: offsetX,
      right: offsetX + width,
      bottom: offsetY + height,
      x: offsetX,
      y: offsetY,
    };
  }

  closeConf() {
    this.setState({
      popups: this.state.popups.filter(popup => popup.type !== types.CONF),
    });
  }

  closeErrors() {
    this.setState({
      popups: this.state.popups.filter(popup => popup.type !== types.ERROR),
    });
  }

  openPopup({ sprite, Component = Popup, content, type, props = {} }) {
    setTimeout(() => {
      props.alignment = props.alignment || TOP;

      const key = `${type}-${sprite.item.itemid}`;

      if (this.state.popups.some(popup => popup.key === key)) {
        return;
      }

      this.setState({
        popups: this.state.popups.concat({
          Component,
          type,
          key,
          content,
          sprite,
          item: sprite.item,
          props: {
            ...props,
            calculateTargetRect: this.calculateTargetRect.bind(this, sprite),
          },
        }),
      });
    }, 0);
  }

  onPopupClose = key => {
    const popups = this.state.popups;
    const index = popups.findIndex(popup => popup.key === key);

    // TODO Place conf dialog in redux state instead
    const popup = popups[index];
    if (popup && popup.type === types.CONF) {
      emitter.emit(DISMISS_POPUP);
      this.props.confClosed(popup.item.itemid);
      this.previousConfTimeout = setTimeout(() => {
        this.props.clearPreviousConf();
      }, 300);
    }

    if (index >= 0) {
      this.setState({
        popups: [...popups.slice(0, index), ...popups.slice(index + 1)],
      });
    }
  };

  render() {
    const { popups } = this.state;
    if (!this.props.getItemSceneBounds) {
      return null;
    }

    return (
      <React.Fragment>
        <TransitionGroup>
          {popups.map(popup => (
            <BrorTransition key={popup.key}>
              <popup.Component
                onClose={() => this.onPopupClose(popup.key)}
                {...popup.props}
              >
                {popup.content}
              </popup.Component>
            </BrorTransition>
          ))}
        </TransitionGroup>
      </React.Fragment>
    );
  }
}

export default connect(
  state => ({
    doorsPopupHintTarget: selectShowDoorsPopupHint(state),
    introPopupsVisible: selectIntroPopupsVisible(state),
    pegboardPopupHintTarget: selectPegboardHint(state),
    pegboardPopupShown: selectHasShownPegboardHint(state),
    cuttableMountingRailPopupHintTarget: selectCuttableMountingRailHint(state),
    cuttableMountingRailPopupHintAlignment:
      selectCuttableMountingRailHintAlignment(state),
    cuttableMountingRailPopupShown:
      selectHasShownCuttableMountingRailHint(state),
    extendableConfTarget: selectShowExtendableConf(state),
    extendableConfShown: selectHasShownExtendableConf(state),
    confDialog: selectConfDialog(state),
    userAgent: selectUserAgent(state),
    tac: selectTac(state),
    wallResizerActive: selectIsWallResizerActive(state),
  }),
  dispatch => ({
    confOpened: () => dispatch(actionConfOpened()),
    confClosed: closedId => dispatch(thunkConfClosed(closedId)),
    clearPreviousConf: () => dispatch(actionClearPreviousConf()),
    itemPickedUp: item => dispatch(actionItemPickedUp(item)),
  })
)(ScenePopupManager);
